import React, {
  JSXElementConstructor,
  lazy,
  ReactNode,
  RefObject,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import SidebarContext, { SidebarType } from '../Context/SidebarContext';
import { InlineDrawer } from '@fluentui/react-components';
import { SideBarCloseIcon } from '../SideBar/SideBarCloseIcon';

const LazySupplierSidebarForm = lazy(() => import('../../views/supplier/SupplierSidebarForm'));
const LazyCustomerSidebarForm = lazy(() => import('../../views/customer/CustomerSidebarForm'));
const LazyProductSidebarForm = lazy(() => import('../../views/product/ProductSidebarForm'));

export const SidebarProvider = ({ children }: { children: ReactNode }) => {
  const [sidebarState, setSidebarState] = useState<{
    open: boolean;
    type?: SidebarType;
    entityId?: number;
  }>({ open: false });

  const openSidebar = useCallback((type: SidebarType, entityId: number) => {
    setSidebarState({ open: true, type: type, entityId });
  }, []);

  const closeSidebar = useCallback(() => {
    setSidebarState({ open: false });
  }, []);

  let LazySidebarForm: JSXElementConstructor<any>;
  switch (sidebarState?.type) {
    case SidebarType.ProductForm:
      LazySidebarForm = LazyProductSidebarForm;
      break;
    case SidebarType.SupplierForm:
      LazySidebarForm = LazySupplierSidebarForm;
      break;
    default:
      LazySidebarForm = LazyCustomerSidebarForm;
      break;
  }

  const sidebarContext = useMemo(() => ({ openSidebar, closeSidebar, modal: true }), [openSidebar, closeSidebar]);

  // Used to prevent the sidebar changing the Modal height - Very jarring when you switch between records.
  const childDivRef = useRef<HTMLDivElement>();

  return (
    <SidebarContext.Provider value={sidebarContext}>
      <div style={{ display: 'flex' }}>
        <InlineDrawer
          style={{ minWidth: '300px', maxHeight: `${childDivRef.current?.clientHeight}px` }}
          open={sidebarState?.open}
        >
          <SideBarCloseIcon left={'5px'} />
          {sidebarState?.entityId && <LazySidebarForm id={sidebarState.entityId} sidebar={true} />}
        </InlineDrawer>
        <div ref={childDivRef as RefObject<HTMLDivElement>} style={{ width: '100%' }}>
          {children}
        </div>
      </div>
    </SidebarContext.Provider>
  );
};
